// 2D blendshape-style mouth driven by Oculus visemes: jaw open, lip width, rounding, teeth/tongue visibility.
// Styles: cartoon (round face, big eyes), realistic (skin tones, lips), robot (visor + segmented LED mouth).
export const MOUTH_STYLES = ["cartoon", "realistic", "robot"];

const SHAPES = {
  sil: { open: 0.04, width: 0.55, round: 0, teeth: 0, tongue: 0 },
  PP: { open: 0.02, width: 0.5, round: 0.1, teeth: 0, tongue: 0 },
  FF: { open: 0.12, width: 0.6, round: 0, teeth: 1, tongue: 0 },
  DD: { open: 0.22, width: 0.6, round: 0, teeth: 0.7, tongue: 0.8 },
  kk: { open: 0.3, width: 0.55, round: 0, teeth: 0.4, tongue: 0.3 },
  CH: { open: 0.25, width: 0.5, round: 0.35, teeth: 0.8, tongue: 0 },
  SS: { open: 0.14, width: 0.7, round: 0, teeth: 1, tongue: 0 },
  nn: { open: 0.18, width: 0.6, round: 0, teeth: 0.5, tongue: 0.9 },
  RR: { open: 0.28, width: 0.5, round: 0.3, teeth: 0.3, tongue: 0.5 },
  aa: { open: 0.95, width: 0.7, round: 0.1, teeth: 0.6, tongue: 0.2 },
  E: { open: 0.55, width: 0.85, round: 0, teeth: 0.9, tongue: 0 },
  ih: { open: 0.35, width: 0.9, round: 0, teeth: 0.8, tongue: 0 },
  oh: { open: 0.7, width: 0.45, round: 0.8, teeth: 0.2, tongue: 0 },
  ou: { open: 0.4, width: 0.3, round: 1, teeth: 0, tongue: 0 },
};

function mouthPath(s, cx, cy, scale = 1) {
  const w = (16 + s.width * 34) * scale, h = (2 + s.open * 26) * scale;
  const ux = w * (1 - s.round * 0.45), uy = cy - h * 0.55, ly = cy + h;
  const upper = `M ${cx - ux} ${cy} Q ${cx - ux * 0.5} ${uy - h * 0.2} ${cx} ${uy} Q ${cx + ux * 0.5} ${uy - h * 0.2} ${cx + ux} ${cy}`;
  const lower = `Q ${cx + ux * 0.6} ${ly} ${cx} ${ly} Q ${cx - ux * 0.6} ${ly} ${cx - ux} ${cy} Z`;
  return { d: upper + " " + lower, w, h, ly };
}

function Cartoon({ s, viseme }) {
  const { d, w, h, ly } = mouthPath(s, 60, 62);
  return (
    <>
      <defs><radialGradient id="mf-skin" cx="50%" cy="40%" r="70%"><stop offset="0%" stopColor="hsl(var(--card))" /><stop offset="100%" stopColor="hsl(var(--muted))" /></radialGradient><clipPath id="mf-mouth-c"><path d={d} /></clipPath></defs>
      <circle cx="60" cy="60" r="56" fill="url(#mf-skin)" stroke="hsl(var(--border))" strokeWidth="1.5" />
      <circle cx="42" cy="42" r="5" fill="hsl(var(--foreground))" opacity="0.85" /><circle cx="78" cy="42" r="5" fill="hsl(var(--foreground))" opacity="0.85" />
      <circle cx="44" cy="40" r="1.5" fill="white" /><circle cx="80" cy="40" r="1.5" fill="white" />
      <path d="M 34 33 q 8 -5 16 -1" stroke="hsl(var(--foreground))" strokeWidth="2" fill="none" opacity="0.5" /><path d="M 70 32 q 8 -4 16 1" stroke="hsl(var(--foreground))" strokeWidth="2" fill="none" opacity="0.5" />
      <path d={d} fill="hsl(var(--foreground) / 0.9)" style={{ transition: "d 70ms linear" }} />
      <g clipPath="url(#mf-mouth-c)">
        <rect x={60 - w} y={62 - h * 0.55 - 2} width={w * 2} height={5 + s.teeth * 5} fill="white" opacity={s.teeth * 0.9} />
        <ellipse cx={60} cy={ly - 2} rx={w * 0.5} ry={4 + s.tongue * 6} fill="hsl(var(--primary))" opacity={0.35 + s.tongue * 0.5} />
      </g>
      <path d={d} fill="none" stroke="hsl(var(--primary))" strokeWidth="2.5" style={{ transition: "d 70ms linear" }} />
      <text x="60" y="112" textAnchor="middle" fontSize="9" fontFamily="monospace" fill="hsl(var(--muted-foreground))">{viseme}</text>
    </>
  );
}

function Realistic({ s, viseme }) {
  const { d, w, h, ly } = mouthPath(s, 60, 70, 0.9);
  return (
    <>
      <defs><linearGradient id="mf-face" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#e8b898" /><stop offset="100%" stopColor="#c58e6c" /></linearGradient><clipPath id="mf-mouth-r"><path d={d} /></clipPath></defs>
      <path d="M 60 6 C 92 6 100 40 98 66 C 96 92 80 114 60 114 C 40 114 24 92 22 66 C 20 40 28 6 60 6 Z" fill="url(#mf-face)" stroke="#a9704f" strokeWidth="1" />
      <ellipse cx="44" cy="44" rx="7" ry="4" fill="white" /><ellipse cx="76" cy="44" rx="7" ry="4" fill="white" />
      <circle cx="44" cy="44" r="2.8" fill="#3a2a1e" /><circle cx="76" cy="44" r="2.8" fill="#3a2a1e" />
      <path d="M 36 36 q 8 -4 16 -1" stroke="#5a3b26" strokeWidth="2.2" fill="none" /><path d="M 68 35 q 8 -3 16 1" stroke="#5a3b26" strokeWidth="2.2" fill="none" />
      <path d="M 60 50 q -4 8 -2 12" stroke="#a9704f" strokeWidth="1.2" fill="none" opacity="0.7" />
      <path d={d} fill="#3b1a1a" style={{ transition: "d 70ms linear" }} />
      <g clipPath="url(#mf-mouth-r)">
        <rect x={60 - w} y={70 - h * 0.55 - 2} width={w * 2} height={5 + s.teeth * 5} fill="#f6f1e7" opacity={s.teeth * 0.95} />
        <ellipse cx={60} cy={ly - 2} rx={w * 0.5} ry={4 + s.tongue * 6} fill="#c8474f" opacity={0.5 + s.tongue * 0.5} />
      </g>
      <path d={d} fill="none" stroke="#a64b4b" strokeWidth={3 + s.round * 1.5} strokeLinejoin="round" style={{ transition: "d 70ms linear" }} />
      <text x="60" y="118" textAnchor="middle" fontSize="8" fontFamily="monospace" fill="hsl(var(--muted-foreground))">{viseme}</text>
    </>
  );
}

function Robot({ s, viseme }) {
  const segs = 7, lit = Math.round(s.open * segs), half = (12 + s.width * 26);
  return (
    <>
      <rect x="14" y="14" width="92" height="92" rx="14" fill="hsl(var(--muted))" stroke="hsl(var(--border))" strokeWidth="2" />
      <rect x="24" y="30" width="72" height="16" rx="6" fill="#0b1220" stroke="hsl(var(--primary))" strokeWidth="1.5" />
      <rect x={30 + s.round * 6} y="35" width={12 - s.round * 4} height="6" rx="2" fill="hsl(var(--primary))" /><rect x={78 - s.round * 2} y="35" width={12 - s.round * 4} height="6" rx="2" fill="hsl(var(--primary))" />
      <rect x={60 - half} y="58" width={half * 2} height="30" rx="5" fill="#0b1220" stroke="hsl(var(--border))" />
      {Array.from({ length: segs }).map((_, i) => {
        const on = i < lit || (i === 0 && s.teeth > 0.5);
        return <rect key={i} x={60 - half + 4} y={84 - i * 3.6 - 3} width={half * 2 - 8} height="2.6" rx="1" fill={on ? "hsl(var(--primary))" : "hsl(var(--primary) / 0.15)"} style={{ transition: "fill 70ms linear" }} />;
      })}
      <text x="60" y="104" textAnchor="middle" fontSize="9" fontFamily="monospace" fill="hsl(var(--primary))">{viseme}</text>
    </>
  );
}

export default function MouthPreview({ viseme = "sil", size = 120, playing = false, style = "cartoon" }) {
  const s = SHAPES[viseme] || SHAPES.sil;
  const Face = style === "realistic" ? Realistic : style === "robot" ? Robot : Cartoon;
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" className={playing ? "" : "opacity-80"} data-testid="mouth-preview" data-viseme={viseme} data-style={style}>
      <Face s={s} viseme={viseme} />
    </svg>
  );
}
